import { useLocation } from 'react-router-dom';
import { blogs } from '../_blog/routes';
import landingGroups from '../content/landingPaths.json';
import { PUBLIC_URL } from './const';
import { Locale, isLocale, pathLocale } from './seo';

const STATIC_PAGES = new Set([
    '/',
    '/blog',
    '/mcp',
    '/for-freelancers',
    '/privacy',
    '/tos',
    '/google-privacy',
    '/login',
    '/dashboard',
    '/admin',
    '/plan',
    '/payment',
    '/book',
    '/mcp-done',
    '/404',
]);

function stripDev(pathname: string): string {
    if (pathname === '/dev') {
        return '/';
    }
    if (pathname.startsWith('/dev/')) {
        return pathname.slice(4);
    }
    return pathname || '/';
}

function withoutTrailingSlash(path: string): string {
    if (path.length > 1 && path.endsWith('/')) {
        return path.slice(0, -1);
    }
    return path;
}

/** Locale from the URL prefix, or from a blog post whose path has no prefix. */
export function contentLocale(pathname: string): Locale | null {
    const fromPath = pathLocale(pathname);
    if (fromPath) {
        return fromPath;
    }
    const full = withoutTrailingSlash(stripDev(pathname));
    for (const group of Object.values(blogs)) {
        for (const [lang, entry] of Object.entries(group as Record<string, { url?: string }>)) {
            if (!isLocale(lang) || !entry?.url) {
                continue;
            }
            if (withoutTrailingSlash(entry.url) === full) {
                return lang;
            }
        }
    }
    return null;
}

function unlocalizedPath(pathname: string): string {
    const parts = stripDev(pathname).split('/').filter(Boolean);
    if (isLocale(parts[0])) {
        parts.shift();
    }
    if (parts.length === 0) {
        return '/';
    }
    return withoutTrailingSlash(`/${parts.join('/')}`);
}

export function localizedPublicPath(currentPathname: string, to: string): string {
    const hashIdx = to.indexOf('#');
    const hash = hashIdx >= 0 ? to.slice(hashIdx) : '';
    const beforeHash = hashIdx >= 0 ? to.slice(0, hashIdx) : to;
    const queryIdx = beforeHash.indexOf('?');
    const query = queryIdx >= 0 ? beforeHash.slice(queryIdx) : '';
    let path = queryIdx >= 0 ? beforeHash.slice(0, queryIdx) : beforeHash;
    if (!path.startsWith('/')) {
        path = `/${path}`;
    }
    const lang = contentLocale(currentPathname);
    const prefix = !lang || lang === 'en' ? '' : `/${lang}`;
    const trimmed = withoutTrailingSlash(path);
    const withSlash = trimmed === '/' ? '/' : `${trimmed}/`;
    const localized = prefix === '' ? withSlash : `${prefix}${withSlash === '/' ? '/' : withSlash}`;
    return `${PUBLIC_URL}${localized}${query}${hash}`;
}

export function useLocalizedPath() {
    const { pathname } = useLocation();
    return (to: string) => localizedPublicPath(pathname, to);
}

export function pathForLanguage(pathname: string, target: Locale): string {
    const full = withoutTrailingSlash(stripDev(pathname));

    for (const group of landingGroups) {
        const paths = group.paths as Record<Locale, string>;
        if (Object.values(paths).some((path) => withoutTrailingSlash(path) === full)) {
            const targetPath = paths[target];
            return targetPath.endsWith('/') ? targetPath : `${targetPath}/`;
        }
    }

    for (const group of Object.values(blogs)) {
        const entries = Object.values(group) as { url: string }[];
        if (!entries.some((entry) => withoutTrailingSlash(entry.url) === full)) {
            continue;
        }
        const translated = (group as Record<string, { url: string }>)[target];
        return (translated ?? (group as Record<string, { url: string }>).en).url;
    }

    const base = unlocalizedPath(pathname);
    if (base === '/' || STATIC_PAGES.has(base)) {
        if (target === 'en') {
            return base === '/' ? '/' : `${base}/`;
        }
        return base === '/' ? `/${target}/` : `/${target}${base}/`;
    }

    return target === 'en' ? '/' : `/${target}/`;
}

export function useLanguageHref() {
    const { pathname } = useLocation();
    return (target: Locale) => `${PUBLIC_URL}${pathForLanguage(pathname, target)}`;
}
